import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrencies, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaMyAccountPagesNavEvent } from "@stoneage/App/Utilities/DataLayer";
import { xScreen } from "@stoneage/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
    showSignOutLink = "showSignOutLink",
}

const enum linkFields {
    url = "url",
    linkText = "linkText",
    openInNewTab = "openInNewTab",
    addBottomBorder = "addBottomBorder",
}

type LinkModel = {
    fields: {
        url: LinkFieldValue;
        linkText: string;
        openInNewTab: boolean;
        addBottomBorder: boolean;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.showSignOutLink]: boolean;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const customSettings = getSettingsCollection(state).customSettings as any;
    return {
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        isPunchOutSession: getIsPunchOutSession(state),
        currentPage: getCurrentPage(state),
        customSettings,
        session: state.context.session,
        myAccountPagesLinks: {
            orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage")?.url,
            invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage")?.url,
            myQuotesPageLink: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
            myProfilePageLink: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            userAdministrationPageLink: getPageLinkByPageType(state, "UserListPage")?.url,
            sentinelPageLink: customSettings?.sentinelHeaderUrl,
            courseManagementPageLink: getPageLinkByPageType(state, "CourseManagementPage")?.url,
            myCoursesPageLink: getPageLinkByPageType(state, "MyCoursesPage")?.url,
        },
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    };
};

interface ListStyleProp {}

const ListStyle = styled.ul<ListStyleProp>`
    li {
        list-style: none;
    }
`;
const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};
export interface HeaderSignInStyles {
    separator?: InjectableCss;
    mobileNavWrapper?: InjectableCss;
    select?: SelectPresentationProps;
    mobileTitleTypo?: TypographyPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    separator: {
        css: css`
            padding-bottom: 15px;
            margin-bottom: 15px;
            border-bottom: 1px solid ${getColor("common.ui5")};
        `,
    },
    mobileNavWrapper: {
        css: css`
            background-color: ${getColor("common.ui4")};
            padding: 16px;
            display: flex;
            align-items: center;
            width: 100%;
            margin-bottom: 46px;
            flex-wrap: wrap;
        `,
    },
    mobileTitleTypo: {
        css: css`
            margin-right: 26px;
            margin-bottom: 0;
        `,
    },
    select: {
        cssOverrides: {
            formField: css`
                max-width: 256px;

                ${xScreen("lt", "sm")} {
                    max-width: 220px;
                }
                ${xScreen("lt", "xs")} {
                    max-width: 100%;
                    margin-top: 15px;
                }
            `,
        },
    },
};
const styles = headerSignInStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const NavigationList = (props: Props) => {
    const {
        fields,
        history,
        currentPage,
        isPunchOutSession,
        cancelPunchOut,
        signOut,
        session,
        customSettings,
        myAccountPagesLinks,
        customerNumber,
        impersonation,
    } = props;

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const customCssWrapper = {
        css: css`
            .navigation-list {
                .navigation-list-item {
                    list-style: none;
                }
            }
            ${props?.fields?.customCSS}
        `,
    };

    const isSentinelUser = session?.properties?.isSentinelUser === "true" || false;
    const isThinkificUser = session?.properties?.isThinkificUser === "true" || false;
    const sentinelURL = customSettings?.sentinelHeaderUrl;
    const thinkificlURL = customSettings?.thinkificHeaderUrl;

    const signOutElement = fields.showSignOutLink ? (
        <Link
            className="signout-clickable"
            data-test-selector="user_signout"
            aria-label="sign out"
            onClick={onSignOutHandler}
            color="secondary"
        >
            {translate("Sign Out")}
        </Link>
    ) : null;
    const links = fields?.links?.length > 0 ? useGetLinks(fields.links, o => o.fields.url) : [];

    const handleSelectChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
        event.preventDefault();
        const url = event.currentTarget.value;
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            window.open(url, "_blank");
        } else {
            history.push(url);
        }
    };

    if (!links) {
        <StyledWrapper {...customCssWrapper}>
            <Hidden below="lg">{signOutElement}</Hidden>
            <Hidden above="md">
                <Select onChange={onSignOutHandler} {...styles.select}>
                    <option> {translate("Sign Out")}</option>
                </Select>
            </Hidden>
        </StyledWrapper>;
    }

    return (
        <StyledWrapper {...customCssWrapper}>
            <Hidden below="lg">
                <ListStyle className="navigation-list" data-test-selector="navigationList">
                    {links.map((link: any, index) => {
                        const fieldLink = fields.links[index].fields;
                        if (
                            (link.url === sentinelURL && !isSentinelUser) ||
                            (link.url === thinkificlURL && !isThinkificUser)
                        ) {
                            return null;
                        }

                        if (link.excludeFromNavigation) {
                            return null;
                        }

                        return (
                            <li key={link.id} className="navigation-list-item">
                                <Link
                                    className="navigation-list-link"
                                    data-test-selector={`navigationList-${link.title}`}
                                    href={link.url}
                                    target={fieldLink.openInNewTab ? "_blank" : ""}
                                    color="secondary"
                                    onClick={() => {
                                        // XNG-Change: track GA4
                                        trackGaMyAccountPagesNavEvent(
                                            null,
                                            link.url,
                                            fieldLink.linkText || link.title,
                                            impersonation,
                                            customerNumber,
                                            myAccountPagesLinks,
                                        );
                                    }}
                                >
                                    {fieldLink.linkText || link.title}
                                </Link>
                                {fieldLink.addBottomBorder && <StyledWrapper {...styles.separator}></StyledWrapper>}
                            </li>
                        );
                    })}
                </ListStyle>
                {signOutElement}
            </Hidden>
            <Hidden above="md">
                <StyledWrapper {...styles.mobileNavWrapper}>
                    <Typography variant="h4" {...styles.mobileTitleTypo}>
                        {translate("Account Navigation")}
                    </Typography>
                    <Select onChange={handleSelectChange} {...styles.select}>
                        {links.map((link: any, index) => {
                            const fieldLink = fields.links[index].fields;
                            if (
                                (link.url === sentinelURL && !isSentinelUser) ||
                                (link.url === thinkificlURL && !isThinkificUser)
                            ) {
                                return null;
                            }

                            if (link.excludeFromNavigation) {
                                return null;
                            }

                            return (
                                <option key={link.id} value={link.url} selected={currentPage.type === link.type}>
                                    {fieldLink.linkText || link.title}
                                </option>
                            );
                        })}
                    </Select>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
.signout-clickable {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(NavigationList)),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                tab: basicTab,
                getDisplay: (item: HasFields, state) => {
                    const {
                        url: { type, value },
                        linkText,
                    } = item.fields;
                    if (linkText) {
                        return linkText;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                fieldDefinitions: [
                    {
                        name: linkFields.url,
                        editorTemplate: "LinkField",
                        isRequired: true,
                        defaultValue: {
                            type: "Page",
                            value: "",
                        },
                    },
                    {
                        name: linkFields.linkText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                        validate: (value, item) => (value.length > 40 ? "max allowed characters is 40" : null),
                    },
                    {
                        name: linkFields.openInNewTab,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: linkFields.addBottomBorder,
                        displayName: "Add Bottom Border",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.showSignOutLink,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
